import { useCallback, useEffect, useRef, useState } from 'react';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import * as intersectsOperator from '@arcgis/core/geometry/operators/intersectsOperator';
import { selectedPolygonSymbol, secondaryPolygonSymbol, setDisableMapClick } from '@origo/map-context';
import { useAppDispatch } from '../../../store/storeHooks';

/**
 * Velg delområder direkte i kartet — som supplement til avkrysningslisten i
 * delområdegruppe-editoren. Brukeren kan
 *   • klikke ett delområde for å veksle det av/på, og
 *   • dra et rektangel for å velge flere (hold Shift mens du drar for å fjerne).
 *
 * Hooken er kontrollert: panelet eier `selected`-settet, hooken melder bare
 * tilbake neste sett via `onChange`. Kart-treff testes in-memory mot polygonene
 * vi allerede har på hvert delområde (`Område.geometry`), så vi slipper
 * feature-layer-spørringer og felt-mapping.
 *
 * Markeringen tegnes på et DEDIKERT GraphicsLayer — aldri `mapView.graphics`,
 * som eies av AdminPage.highlightContext og tømmes med removeAll().
 */

const HIGHLIGHT_LAYER_ID = 'group-editor-selection';
const DRAW_LAYER_ID = 'group-editor-draw';

/** Hvor lenge etter en ferdig firkant vi ignorerer et etterfølgende klikk. */
const CLICK_SUPPRESS_MS = 350;

export interface DelområdeMapCandidate {
    id: string;
    geometry?: __esri.Polygon;
}

export interface UseDelområdeMapSelectionProps {
    /** Live MapView/SceneView fra useMapViewContext(). null til kartet er klart. */
    mapView: __esri.MapView | __esri.SceneView | null | undefined;
    /** Valgbare delområder for aktiv type, med geometri. */
    candidates: readonly DelområdeMapCandidate[];
    /** Gjeldende seleksjon (samme Set som avkrysningslisten styrer). */
    selected: ReadonlySet<string>;
    /** IDer som er medlem av en annen gruppe — ignoreres i kart-interaksjon. */
    blocked?: ReadonlySet<string>;
    /** Melder neste seleksjon (én mutasjon, dekker både klikk og firkant). */
    onChange: (next: Set<string>) => void;
    /** Hovedbryter — handlers kobles kun på mens panelet er aktivt. */
    enabled: boolean;
}

export interface UseDelområdeMapSelectionReturn {
    /** Sant når lag + handlers er koblet opp. */
    ready: boolean;
    /** Sant mens en firkant-tegning pågår. */
    isDrawingRectangle: boolean;
    /** Start én rektangel-tegning (Shift holdes nede ved drag → fjern). */
    startRectangle: () => void;
    /** Avbryt en pågående rektangel-tegning. */
    cancelRectangle: () => void;
    /** Zoom kartet til de valgte delområdene. */
    zoomToSelection: () => void;
    /** Zoom kartet til ett bestemt delområde. */
    zoomTo: (id: string) => void;
}

const extentArea = (g?: __esri.Polygon): number => {
    const e = g?.extent;
    if (!e) return Number.POSITIVE_INFINITY;
    return e.width * e.height;
};

export function useDelområdeMapSelection({
    mapView,
    candidates,
    selected,
    blocked,
    onChange,
    enabled,
}: UseDelområdeMapSelectionProps): UseDelområdeMapSelectionReturn {
    const dispatch = useAppDispatch();

    const highlightLayerRef = useRef<GraphicsLayer | null>(null);
    const drawLayerRef = useRef<GraphicsLayer | null>(null);
    const sketchVMRef = useRef<SketchViewModel | null>(null);
    const clickHandleRef = useRef<IHandle | null>(null);

    // Refs holder ferske verdier inne i kart-handlers uten å re-registrere dem.
    const selectedRef = useRef(selected);
    const blockedRef = useRef(blocked);
    const onChangeRef = useRef(onChange);
    const drawingRef = useRef(false);
    const rectRemoveRef = useRef(false);
    const shiftHeldRef = useRef(false);
    const suppressClickUntilRef = useRef(0);

    // Delområde-geometriene hentes nå i kartets SR (se useAdminAreas/outputWkid),
    // så vi reprojiserer ikke lenger på klienten. intersectsOperator krever lik
    // SR og får det. Ref-en holder ferske kandidater inne i kart-handlers uten å
    // re-registrere dem.
    const candidatesRef = useRef<readonly DelområdeMapCandidate[]>(candidates);
    useEffect(() => { candidatesRef.current = candidates; }, [candidates]);

    useEffect(() => { selectedRef.current = selected; }, [selected]);
    useEffect(() => { blockedRef.current = blocked; }, [blocked]);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    const [ready, setReady] = useState(false);
    const [isDrawingRectangle, setIsDrawingRectangle] = useState(false);

    // ── Lag + SketchViewModel (livssyklus følger mapView) ──
    useEffect(() => {
        const map = mapView?.map;
        if (!mapView || !map) return;

        let destroyed = false;
        const handles: IHandle[] = [];

        let highlightLayer = map.findLayerById(HIGHLIGHT_LAYER_ID) as GraphicsLayer;
        if (!highlightLayer) {
            highlightLayer = new GraphicsLayer({ id: HIGHLIGHT_LAYER_ID, listMode: 'hide' });
            map.add(highlightLayer);
        }
        highlightLayerRef.current = highlightLayer;

        let drawLayer = map.findLayerById(DRAW_LAYER_ID) as GraphicsLayer;
        if (!drawLayer) {
            drawLayer = new GraphicsLayer({ id: DRAW_LAYER_ID, listMode: 'hide' });
            map.add(drawLayer);
        }
        drawLayerRef.current = drawLayer;

        mapView.when(() => {
            if (destroyed) return;
            const vm = new SketchViewModel({ layer: drawLayer, view: mapView });
            sketchVMRef.current = vm;

            handles.push(vm.on('create', (event) => {
                if (event.state === 'start') {
                    // «Shift+dra» avgjøres av Shift-tilstanden når draget begynner.
                    rectRemoveRef.current = shiftHeldRef.current;
                    return;
                }
                if (event.state === 'cancel') {
                    drawLayer.removeAll();
                    drawingRef.current = false;
                    setIsDrawingRectangle(false);
                    return;
                }
                if (event.state !== 'complete') return;

                const rect = event.graphic?.geometry as __esri.Polygon | undefined;
                drawLayer.removeAll();
                suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESS_MS;
                drawingRef.current = false;
                setIsDrawingRectangle(false);
                if (!rect) return;

                const remove = rectRemoveRef.current;
                const next = new Set(selectedRef.current);
                for (const c of candidatesRef.current) {
                    if (!c.geometry) continue;
                    // Medlem av en ANNEN gruppe (blokkert og ikke allerede valgt) hoppes over.
                    if (blockedRef.current?.has(c.id) && !next.has(c.id)) continue;
                    if (intersectsOperator.execute(rect, c.geometry)) {
                        if (remove) next.delete(c.id);
                        else next.add(c.id);
                    }
                }
                onChangeRef.current(next);
            }) as unknown as IHandle);

            setReady(true);
        });

        return () => {
            destroyed = true;
            handles.forEach((h) => h.remove());
            sketchVMRef.current?.cancel();
            sketchVMRef.current?.destroy();
            sketchVMRef.current = null;
            setReady(false);
            if (highlightLayerRef.current) {
                map.remove(highlightLayerRef.current);
                highlightLayerRef.current.destroy();
                highlightLayerRef.current = null;
            }
            if (drawLayerRef.current) {
                map.remove(drawLayerRef.current);
                drawLayerRef.current.destroy();
                drawLayerRef.current = null;
            }
        };
    }, [mapView]);

    // ── Klikk-handler + Shift-sporing + disableMapClick ──
    useEffect(() => {
        if (!enabled || !mapView) return;
        if (clickHandleRef.current) return;

        // Demp appens standard kart-klikk (popup/lokasjon) mens vi velger.
        dispatch(setDisableMapClick(true));

        const onKey = (e: KeyboardEvent) => { shiftHeldRef.current = e.shiftKey; };
        window.addEventListener('keydown', onKey);
        window.addEventListener('keyup', onKey);

        clickHandleRef.current = mapView.on('click', (event: __esri.ViewClickEvent) => {
            if (drawingRef.current) return;
            if (Date.now() < suppressClickUntilRef.current) return;
            const point = event.mapPoint;
            if (!point) return;

            // Treff blant kandidatene; ved overlapp velg minste (mest spesifikke).
            // Medlem av en annen gruppe (blokkert, ikke allerede valgt) hoppes over,
            // men et nåværende medlem (valgt) kan klikkes av for å fjernes.
            const hits = candidatesRef.current
                .filter((c) => c.geometry && intersectsOperator.execute(point, c.geometry))
                .filter((c) => !(blockedRef.current?.has(c.id) && !selectedRef.current.has(c.id)));
            if (hits.length === 0) return;
            hits.sort((a, b) => extentArea(a.geometry) - extentArea(b.geometry));
            const id = hits[0].id;

            const next = new Set(selectedRef.current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            onChangeRef.current(next);
        });

        return () => {
            clickHandleRef.current?.remove();
            clickHandleRef.current = null;
            sketchVMRef.current?.cancel();
            drawingRef.current = false;
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('keyup', onKey);
            dispatch(setDisableMapClick(false));
        };
    }, [enabled, mapView, dispatch]);

    // ── Tegn markering for alle valgbare delområder ──
    // Alle kandidater vises i utgangspunktet med kun omriss (ingen fyllfarge);
    // valgte får et tydelig dypere blått fyll. Det gir et klart visuelt skille
    // mellom valgt og ikke-valgt. Fargen hentes fra --map-selection-tokenet
    // (via selectedPolygonSymbol) så den følger tema/dark-mode.
    useEffect(() => {
        const layer = highlightLayerRef.current;
        if (!layer) return;
        layer.removeAll();
        if (!enabled) return;

        const [r, g, b] = selectedPolygonSymbol().color;
        const selectedSymbol = {
            type: 'simple-fill' as const,
            color: [r, g, b, 0.4],
            outline: { color: [r, g, b, 1], width: 2 },
        };
        const candidateSymbol = {
            type: 'simple-fill' as const,
            color: [r, g, b, 0],
            outline: { color: [r, g, b, 0.7], width: 1.5 },
        };
        const blockedSymbol = secondaryPolygonSymbol();

        // Bygg alle grafikkene først og addMany i ett kall. .add() i loop trigger
        // én render-runde per element; for en delområdetype med hundrevis av
        // delområder (kommuneplan) fryser det hovedtråden.
        const graphics: Graphic[] = [];
        for (const c of candidates) {
            if (!c.geometry) continue;
            // Valgt vinner alltid (et nåværende medlem er både «blocked» og valgt).
            if (selected.has(c.id)) {
                graphics.push(new Graphic({
                    geometry: c.geometry,
                    symbol: selectedSymbol as never,
                    attributes: { delområdeId: c.id },
                }));
            } else if (blocked?.has(c.id)) {
                graphics.push(new Graphic({ geometry: c.geometry, symbol: blockedSymbol as never }));
            } else {
                graphics.push(new Graphic({ geometry: c.geometry, symbol: candidateSymbol as never }));
            }
        }
        layer.addMany(graphics);
    }, [selected, candidates, blocked, enabled, ready]);

    const startRectangle = useCallback(() => {
        const vm = sketchVMRef.current;
        if (!vm) return;
        vm.cancel();
        drawingRef.current = true;
        setIsDrawingRectangle(true);
        vm.create('rectangle');
    }, []);

    const cancelRectangle = useCallback(() => {
        sketchVMRef.current?.cancel();
        drawingRef.current = false;
        setIsDrawingRectangle(false);
    }, []);

    const zoomToSelection = useCallback(() => {
        if (!mapView) return;
        const geoms = candidatesRef.current
            .filter((c) => c.geometry && selectedRef.current.has(c.id))
            .map((c) => c.geometry!);
        if (geoms.length === 0) return;
        mapView.goTo(geoms, { duration: 400, easing: 'ease-out' }).catch(() => { /* avbrutt navigasjon */ });
    }, [mapView]);

    const zoomTo = useCallback((id: string) => {
        if (!mapView) return;
        const geom = candidatesRef.current.find((c) => c.id === id)?.geometry;
        if (!geom) return;
        // Utvid utstrekningen litt så delområdet ikke fyller hele kartet.
        const target = geom.extent ? geom.extent.clone().expand(1.6) : geom;
        mapView.goTo(target, { duration: 400, easing: 'ease-out' }).catch(() => { /* avbrutt navigasjon */ });
    }, [mapView]);

    return { ready, isDrawingRectangle, startRectangle, cancelRectangle, zoomToSelection, zoomTo };
}
